/**
 * The system share sheet (Web Share API), lifted from the blog share buttons
 * so the tutor's Remind and Ask for photo can hand a message to WhatsApp from
 * a phone in one tap.
 *
 * Two gates, deliberately separate:
 * - `supportsNativeShare`: the API exists and does not refuse this payload.
 *   Desktop Safari passes it too, which is right for the blog's "Share this
 *   article" (a macOS share sheet is a fine outcome there).
 * - `canUseNativeShare`: additionally a phone or tablet (a coarse primary
 *   pointer with touch points). The tutor surfaces promise a copy on desktop
 *   and a share sheet on a phone, so API presence alone would open a share
 *   sheet on a Mac where the plan promises "Copied" (eng addendum finding 9).
 *
 * `shareNative` must run synchronously inside the tap handler: browsers only
 * open the sheet with transient user activation, so nothing may be awaited
 * before it.
 */

export type NativeShareOutcome = 'shared' | 'cancelled' | 'failed';

/** A coarse primary pointer on a touch device: a phone or a tablet. */
export function hasCoarsePointer(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  const coarse =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;
  return coarse && (navigator.maxTouchPoints ?? 0) > 0;
}

/** The Web Share API is present and accepts this payload. */
export function supportsNativeShare(data: ShareData): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function') return false;
  if (typeof navigator.canShare !== 'function') return true;
  try {
    return navigator.canShare(data) !== false;
  } catch {
    return false;
  }
}

/** A phone or tablet whose share sheet accepts this payload. */
export function canUseNativeShare(data: ShareData): boolean {
  return hasCoarsePointer() && supportsNativeShare(data);
}

/**
 * Open the share sheet. `cancelled` when the user dismissed it (AbortError),
 * `failed` for anything else (no activation, a refused payload), so a caller
 * can fall back instead of stamping a share that never happened.
 */
export async function shareNative(
  data: ShareData
): Promise<NativeShareOutcome> {
  try {
    await navigator.share(data);
    return 'shared';
  } catch (error) {
    return (error as { name?: unknown } | null)?.name === 'AbortError'
      ? 'cancelled'
      : 'failed';
  }
}

/** Share plain text (a message the tutor edits in WhatsApp before sending). */
export function shareText(text: string): Promise<NativeShareOutcome> {
  return shareNative({ text });
}

/** `https://wa.me/?text=<encoded>`: WhatsApp's chooser with the message typed out. */
export function whatsAppShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}
